import React, { useState } from 'react';
import { 
  Card, 
  CardBody, 
  CardFooter, 
  Container, 
  Button, 
  StatusBadge, 
  Tag, 
  Divider,
  Section,
  SectionTitle,
  Grid,
  GridItem,
  Flex
} from '../../../styles/components';
import styled, { keyframes } from 'styled-components';

// Animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

const orbitAnimation = keyframes`
  0% { transform: rotate(0deg) translateX(3px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(3px) rotate(-360deg); }
`;

// Enhanced Container with space background
const SpaceContainer = styled(Container)`
  position: relative;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  background: rgba(15, 23, 42, 0.25);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    opacity: 0.15;
    background-image: 
      radial-gradient(2px 2px at 40px 60px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 100px 150px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 200px 80px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 300px 250px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 400px 100px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 500px 200px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 50px 300px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 250px 350px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 550px 350px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0));
  }
`;

// Styled components specific to this component
const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    z-index: -1;
    background: 
      radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(124, 58, 237, 0.05) 0%, transparent 50%);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #fff;
  margin-right: 2rem;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
  animation: ${floatAnimation} 5s infinite ease-in-out;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    right: -10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(99, 102, 241, 0.7);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.8);
    animation: ${orbitAnimation} 8s infinite linear;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), rgba(124, 58, 237, 0.8));
    border-radius: 3px;
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
  }
`;

const ProfileId = styled.p`
  margin: 1rem 0 0.5rem 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
`;

const ProfileContact = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const InfoCard = styled(Card)`
  height: 100%;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 15px rgba(99, 102, 241, 0.2);
  }
`;

const InfoItem = styled.div`
  margin-bottom: 1.25rem;
  position: relative;
  padding-left: 0.5rem;
  
  &:before {
    content: '';
    position: absolute;
    left: -5px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, rgba(99, 102, 241, 0.7), rgba(124, 58, 237, 0.3));
    border-radius: 2px;
    opacity: 0.5;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`;

const ActionButton = styled(Button)`
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SpaceTag = styled(Tag)`
  background: rgba(15, 23, 42, 0.5);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  }
`;

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const SpaceDivider = styled(Divider)`
  background: linear-gradient(90deg, 
    rgba(15, 23, 42, 0), 
    rgba(99, 102, 241, 0.3) 50%, 
    rgba(15, 23, 42, 0) 100%
  );
  height: 1px;
  margin: 2rem 0;
`;

const SectionHeader = styled(SectionTitle)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), rgba(99, 102, 241, 0.1));
    border-radius: 2px;
  }
`;

const StudentProfile = ({ studentId, onBack }) => {
  // State for modals
  const [showInternshipDetails, setShowInternshipDetails] = useState(false);
  const [showReportDetails, setShowReportDetails] = useState(false);
  const [selectedInternship, setSelectedInternship] = useState(null);
  
  // State for internship filter
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'current', 'completed'
  
  // Styled components for the filter
  const FilterContainer = styled.div`
    margin: 1rem 0 2rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  `;
  
  const FilterButton = styled(Button)`
    padding: 0.4rem 0.8rem;
    background: ${props => props.active ? 'rgba(99, 102, 241, 0.7)' : 'rgba(15, 23, 42, 0.5)'};
    color: white;
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 6px;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(99, 102, 241, 0.6);
      transform: translateY(-2px);
    }
  `;
  
  // Dummy data for student profile
  // In a real application, this would be fetched based on the studentId
  const student = {
    id: 'S-2025-001',
    name: 'Ahmed Mohamed',
    email: 'ahmed.mohamed@student.guc.edu.eg',
    phone: '+20 123 456 7890',
    major: 'Computer Science',
    semester: 6,
    gpa: 3.75,
    internshipStatus: 'current',
    skills: ['JavaScript', 'React', 'Node.js', 'HTML/CSS', 'MongoDB', 'Express'],
    avatar: 'AM',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
    address: 'New Cairo, Cairo, Egypt',
    dateOfBirth: '1999-05-15',
    enrollmentDate: '2021-09-01',
    expectedGraduation: '2025-06-30',
    currentInternship: {
      company: 'Tech Solutions Inc.',
      companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
      position: 'Frontend Developer Intern',
      startDate: '2025-01-15',
      endDate: null, // Ongoing internship with no end date
      status: 'current',
      supervisor: 'Mohamed Ahmed',
      supervisorEmail: 'mohamed.ahmed@techsolutions.com',
      supervisorPhone: '+20 123 987 6543',
      supervisorImage: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
      description: 'Working on developing responsive user interfaces using React and implementing API integrations for a customer management system.',
      technologies: ['React', 'Redux', 'REST APIs', 'Material UI'],
      achievements: [
        'Implemented a new dashboard interface that improved user engagement by 25%',
        'Reduced application load time by 40% through code optimization',
        'Created reusable component library for future projects'
      ]
    },
    academicAdvisor: 'Dr. Khaled Ibrahim',
    previousInternships: [
      {
        company: 'Digital Innovations',
        companyLogo: 'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        position: 'Web Development Intern',
        startDate: '2024-06-01',
        endDate: '2024-08-31',
        status: 'completed',
        supervisor: 'Laila Hassan',
        supervisorEmail: 'laila@digitalinnovations.com',
        supervisorPhone: '+20 123 456 7777',
        supervisorImage: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        description: 'Worked on developing and maintaining web applications for clients in the e-commerce sector.',
        technologies: ['HTML', 'CSS', 'JavaScript', 'PHP'],
        achievements: [
          'Developed a responsive e-commerce website for a local business',
          'Implemented payment gateway integrations',
          'Optimized database queries improving site performance'
        ],
        report: {
          title: 'Web Development Internship at Digital Innovations',
          submissionDate: '2024-09-15',
          status: 'accepted',
          content: 'This report details my experience as a Web Development Intern at Digital Innovations during the summer of 2024. Throughout the internship, I gained valuable experience in web development technologies and best practices.',
          sections: [
            { title: 'Introduction', content: 'Overview of the company and my role' },
            { title: 'Projects', content: 'Details of the projects I worked on' },
            { title: 'Skills Acquired', content: 'New skills and technologies learned' },
            { title: 'Challenges', content: 'Challenges faced and how they were overcome' },
            { title: 'Conclusion', content: 'Summary of the internship experience' }
          ],
          evaluation: {
            technical: 85,
            communication: 90,
            problemSolving: 88,
            teamwork: 92,
            overall: 89
          }
        }
      }
    ]
  };

  // Function to get status badge color
  const getStatusBadge = (status) => {
    switch(status) {
      case 'current':
        return <StatusBadge status="pending">Current Intern</StatusBadge>;
      case 'completed':
        return <StatusBadge status="accepted">Completed</StatusBadge>;
      case 'pending':
        return <StatusBadge status="interview">Application Pending</StatusBadge>;
      case 'none':
        return <StatusBadge status="closed">No Internship</StatusBadge>;
      default:
        return <StatusBadge>Unknown</StatusBadge>;
    }
  };
  
  // Function to format internship duration
  const formatDuration = (startDate, endDate) => {
    const start = new Date(startDate).toLocaleDateString();
    
    if (!endDate) {
      return `${start} - Present`;
    }
    
    const end = new Date(endDate).toLocaleDateString();
    return `${start} - ${end}`;
  };

  // Function to handle viewing internship details
  const handleViewInternshipDetails = () => {
    setSelectedInternship(student.currentInternship);
    setShowInternshipDetails(true);
  };

  // Function to handle viewing report details
  const handleViewReport = (internship) => {
    setSelectedInternship(internship);
    setShowReportDetails(true);
  };

  return (
    <SpaceContainer>
      <ActionButton variant="outline" onClick={onBack}>
        &larr; Back to Students List
      </ActionButton>
      
      <Card>
        <CardBody>
          <ProfileHeader>
            <ProfileAvatar>
              {student.image ? (
                <img src={student.image} alt={student.name} />
              ) : (
                student.avatar
              )}
            </ProfileAvatar>
            <ProfileInfo>
              <ProfileName>{student.name}</ProfileName>
              <ProfileId>{student.id} • {student.major}</ProfileId>
              <div style={{ marginTop: '0.5rem' }}>
                {getStatusBadge(student.internshipStatus)}
              </div>
              <ProfileContact>
                <div>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>{student.email}</InfoValue>
                </div>
                <div>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoValue>{student.phone}</InfoValue>
                </div>
              </ProfileContact>
            </ProfileInfo>
          </ProfileHeader>
          
          <TagGroup>
            {student.skills.map((skill, index) => (
              <SpaceTag key={index} color="primary">{skill}</SpaceTag>
            ))}
          </TagGroup>
        </CardBody>
      </Card>
      
      {/* Internship Filter */}
      <Section>
        <SectionHeader>Internship History</SectionHeader>
        <FilterContainer>
          <FilterButton 
            active={filterStatus === 'all'} 
            onClick={() => setFilterStatus('all')}
          >
            All Internships
          </FilterButton>
          <FilterButton 
            active={filterStatus === 'current'} 
            onClick={() => setFilterStatus('current')}
          >
            Current Intern
          </FilterButton>
          <FilterButton 
            active={filterStatus === 'completed'} 
            onClick={() => setFilterStatus('completed')}
          >
            Internship Complete
          </FilterButton>
        </FilterContainer>
      </Section>
      
      <Section>
        <SectionHeader>Academic Information</SectionHeader>
        <Grid columns={2} mobileColumns={1}>
          <GridItem>
            <InfoCard>
              <CardBody>
                <InfoItem>
                  <InfoLabel>Major</InfoLabel>
                  <InfoValue>{student.major}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Current Semester</InfoLabel>
                  <InfoValue>{student.semester}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>GPA</InfoLabel>
                  <InfoValue>{student.gpa}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Enrollment Date</InfoLabel>
                  <InfoValue>{new Date(student.enrollmentDate).toLocaleDateString()}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Expected Graduation</InfoLabel>
                  <InfoValue>{new Date(student.expectedGraduation).toLocaleDateString()}</InfoValue>
                </InfoItem>
              </CardBody>
            </InfoCard>
          </GridItem>
          <GridItem>
            <InfoCard>
              <CardBody>
                <InfoItem>
                  <InfoLabel>Address</InfoLabel>
                  <InfoValue>{student.address}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Date of Birth</InfoLabel>
                  <InfoValue>{new Date(student.dateOfBirth).toLocaleDateString()}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Academic Advisor</InfoLabel>
                  <InfoValue>{student.academicAdvisor}</InfoValue>
                </InfoItem>
              </CardBody>
            </InfoCard>
          </GridItem>
        </Grid>
      </Section>
      
      {/* Display current internship if it exists and filter matches */}
      {student.currentInternship && (filterStatus === 'all' || filterStatus === 'current') && (
        <Section>
          <SectionHeader>Current Internship</SectionHeader>
          <Card>
            <CardBody>
              <Flex alignItems="center" marginBottom="1rem">
                <ProfileAvatar style={{ width: '60px', height: '60px' }}>
                  <img src={student.currentInternship.companyLogo} alt={student.currentInternship.company} />
                </ProfileAvatar>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0' }}>{student.currentInternship.position}</h3>
                  <p style={{ margin: 0, color: '#666' }}>{student.currentInternship.company}</p>
                </div>
              </Flex>
              <Grid columns={2} mobileColumns={1}>
                <GridItem>
                  <InfoItem>
                    <InfoLabel>Duration</InfoLabel>
                    <InfoValue>
                      {formatDuration(student.currentInternship.startDate, student.currentInternship.endDate)}
                    </InfoValue>
                  </InfoItem>
                </GridItem>
                <GridItem>
                  <InfoItem>
                    <InfoLabel>Supervisor</InfoLabel>
                    <InfoValue>{student.currentInternship.supervisor}</InfoValue>
                  </InfoItem>
                </GridItem>
              </Grid>
            </CardBody>
            <CardFooter>
              <ActionButton variant="primary" onClick={handleViewInternshipDetails}>View Internship Details</ActionButton>
            </CardFooter>
          </Card>
        </Section>
      )}
      
      {/* Display previous internships if they exist and filter matches */}
      {student.previousInternships && student.previousInternships.length > 0 && (filterStatus === 'all' || filterStatus === 'completed') && (
        <Section>
          <SectionHeader>Previous Internships</SectionHeader>
          {student.previousInternships.map((internship, index) => (
            <Card key={index} style={{ marginBottom: '1rem' }}>
              <CardBody>
                <Flex justifyContent="space-between" alignItems="flex-start">
                  <Flex>
                    <ProfileAvatar style={{ width: '40px', height: '40px' }}>
                      <img src={internship.companyLogo} alt={internship.company} />
                    </ProfileAvatar>
                    <div>
                      <h3 style={{ margin: '0 0 0.5rem 0' }}>{internship.position}</h3>
                      <p style={{ margin: '0 0 0.5rem 0' }}>{internship.company}</p>
                      <p style={{ margin: '0', color: '#666' }}>
                        {formatDuration(internship.startDate, internship.endDate)}
                      </p>
                    </div>
                  </Flex>
                  <StatusBadge status="accepted">Completed</StatusBadge>
                </Flex>
              </CardBody>
              <CardFooter>
                <ActionButton variant="outline" onClick={() => handleViewReport(internship)}>View Report</ActionButton>
              </CardFooter>
            </Card>
          ))}
        </Section>
      )}
    
      {/* Internship Details Modal */}
      {showInternshipDetails && selectedInternship && (
        <SpaceContainer>
          <CardBody>
            <ActionButton onClick={() => setShowInternshipDetails(false)}>&times;</ActionButton>
            
            <Flex alignItems="center" marginBottom="1.5rem">
              <ProfileAvatar style={{ width: '60px', height: '60px' }}>
                <img src={selectedInternship.companyLogo} alt={selectedInternship.company} />
              </ProfileAvatar>
              <div>
                <h2 style={{ margin: '0 0 0.25rem 0' }}>{selectedInternship.position}</h2>
                <p style={{ margin: 0, color: '#666' }}>{selectedInternship.company}</p>
              </div>
            </Flex>
            
            <Grid columns={2} mobileColumns={1}>
              <GridItem>
                <InfoItem>
                  <InfoLabel>Start Date</InfoLabel>
                  <InfoValue>{new Date(selectedInternship.startDate).toLocaleDateString()}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>End Date</InfoLabel>
                  <InfoValue>
                    {selectedInternship.endDate ? 
                      new Date(selectedInternship.endDate).toLocaleDateString() : 
                      'Present (Ongoing)'}
                  </InfoValue>
                </InfoItem>
              </GridItem>
              <GridItem>
                <InfoItem>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue>{getStatusBadge(selectedInternship.status || 'current')}</InfoValue>
                </InfoItem>
              </GridItem>
            </Grid>
            
            <SpaceDivider />
            
            <h3>Supervisor Information</h3>
            <Flex alignItems="center" marginBottom="1rem">
              <ProfileAvatar style={{ width: '50px', height: '50px' }}>
                {selectedInternship.supervisorImage ? (
                  <img src={selectedInternship.supervisorImage} alt={selectedInternship.supervisor} />
                ) : (
                  selectedInternship.supervisor.charAt(0)
                )}
              </ProfileAvatar>
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0' }}>{selectedInternship.supervisor}</h4>
                <p style={{ margin: 0, color: '#666' }}>{selectedInternship.supervisorEmail}</p>
                <p style={{ margin: 0, color: '#666' }}>{selectedInternship.supervisorPhone}</p>
              </div>
            </Flex>
            
            <SpaceDivider />
            
            <h3>Internship Description</h3>
            <p>{selectedInternship.description}</p>
            
            <h3>Technologies Used</h3>
            <TagGroup>
              {selectedInternship.technologies && selectedInternship.technologies.map((tech, index) => (
                <SpaceTag key={index}>{tech}</SpaceTag>
              ))}
            </TagGroup>
            
            <h3>Key Achievements</h3>
            <ul>
              {selectedInternship.achievements && selectedInternship.achievements.map((achievement, index) => (
                <li key={index}>{achievement}</li>
              ))}
            </ul>
          </CardBody>
        </SpaceContainer>
      )}

      {/* Report Details Modal */}
      {showReportDetails && selectedInternship && selectedInternship.report && (
        <SpaceContainer>
          <CardBody>
            <ActionButton onClick={() => setShowReportDetails(false)}>&times;</ActionButton>
            
            <h2>{selectedInternship.report.title}</h2>
            <p style={{ color: '#666' }}>Submitted on {new Date(selectedInternship.report.submissionDate).toLocaleDateString()}</p>
            
            <Flex justifyContent="space-between" alignItems="center" marginTop="1rem" marginBottom="1.5rem">
              <Flex alignItems="center">
                <ProfileAvatar style={{ width: '40px', height: '40px' }}>
                  <img src={selectedInternship.companyLogo} alt={selectedInternship.company} />
                </ProfileAvatar>
                <div>
                  <p style={{ margin: 0, fontWeight: 500 }}>{selectedInternship.position}</p>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>{selectedInternship.company}</p>
                </div>
              </Flex>
              {getStatusBadge(selectedInternship.report.status)}
            </Flex>
            
            <SpaceDivider />
            
            <p>{selectedInternship.report.content}</p>
            
            <h3 style={{ marginTop: '1.5rem' }}>Report Sections</h3>
            {selectedInternship.report.sections.map((section, index) => (
              <Card key={index} style={{ marginBottom: '1rem' }}>
                <CardBody>
                  <h4>{section.title}</h4>
                  <p>{section.content}</p>
                </CardBody>
              </Card>
            ))}
            
            <h3 style={{ marginTop: '1.5rem' }}>Evaluation</h3>
            <Grid columns={2} mobileColumns={1}>
              <GridItem>
                <InfoItem>
                  <InfoLabel>Technical Skills</InfoLabel>
                  <InfoValue>{selectedInternship.report.evaluation.technical}/100</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Communication</InfoLabel>
                  <InfoValue>{selectedInternship.report.evaluation.communication}/100</InfoValue>
                </InfoItem>
              </GridItem>
              <GridItem>
                <InfoItem>
                  <InfoLabel>Problem Solving</InfoLabel>
                  <InfoValue>{selectedInternship.report.evaluation.problemSolving}/100</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Teamwork</InfoLabel>
                  <InfoValue>{selectedInternship.report.evaluation.teamwork}/100</InfoValue>
                </InfoItem>
              </GridItem>
            </Grid>
            
            <InfoItem style={{ marginTop: '1rem' }}>
              <InfoLabel>Overall Score</InfoLabel>
              <InfoValue style={{ fontSize: '1.2rem', color: '#4CAF50' }}>{selectedInternship.report.evaluation.overall}/100</InfoValue>
            </InfoItem>
          </CardBody>
        </SpaceContainer>
      )}
    </SpaceContainer>
  );
};

export default StudentProfile;
